/**
 * Brain na demonstração — trilhas B4 e B5.
 *
 * Nenhuma IA é chamada e nada sai do navegador: a resposta é montada aqui, em
 * memória, e diz que é simulada. Ela existe para a tela ter o que mostrar e
 * para os testes exercitarem o fluxo inteiro — anexo, arquivo gerado, projeto,
 * preferência, edição, parar e recuperar e, principalmente, a proposta que só
 * executa com confirmação.
 */

import { ErroDoBrain, PREFERENCIAS_PADRAO, normalizarPreferencias, problemaDoAnexo } from './brain';
import type {
    AcaoDeTexto, AnexoDoBrain, ArquivoGeradoDoBrain, ConversaDoBrain, EnvioAoBrain, IdiomaDoBrain, LinkAprendido,
    MensagemDoBrain, ModelosDoBrain, PedidoDoBrain, PreferenciasDoBrain, ProjetoDoBrain, PropostaDoBrain, RespostaDoBrain,    FalaGerada,
} from './brain';
import type { PortaDeDados } from './porta';

type MetodosDoBrain = Pick<PortaDeDados, Extract<keyof PortaDeDados, `brain${string}`>>;

/** Os mesmos prazos do servidor: proposta vale 30 minutos, conversa 90 dias. */
const VALIDADE_DA_PROPOSTA_MS = 30 * 60 * 1000;
const RETENCAO_MS = 90 * 24 * 60 * 60 * 1000;

interface PropostaGuardada {
    conversaId: string;
    mensagemId: string;
    criadaEm: number;
    proposta: PropostaDoBrain;
}

let conversas: ConversaDoBrain[] = [];
let mensagens = new Map<string, MensagemDoBrain[]>();
let propostas = new Map<string, PropostaGuardada>();
/** Conteúdo dos anexos e dos arquivos gerados, pelo caminho. */
let arquivos = new Map<string, Blob>();
let projetos: ProjetoDoBrain[] = [];
let preferencias: PreferenciasDoBrain = PREFERENCIAS_PADRAO;
/** Respostas guardadas por `pedidoId`, como o servidor guarda por um dia. */
let pedidos = new Map<string, RespostaDoBrain>();
let sequencia = 0;
/** Espera artificial da resposta, para a tela e os testes verem "parar". */
let atrasoMs = 0;

export function reiniciarBrainDaDemonstracao(): void {
    conversas = [];
    mensagens = new Map();
    propostas = new Map();
    arquivos = new Map();
    projetos = [];
    preferencias = PREFERENCIAS_PADRAO;
    pedidos = new Map();
    sequencia = 0;
    atrasoMs = 0;
}

/** Quanto a resposta simulada demora. Zero por padrão. */
export function definirAtrasoDoBrainNaDemonstracao(ms: number): void {
    atrasoMs = Math.max(0, ms);
}

export const MODELOS_DA_DEMONSTRACAO: ModelosDoBrain = {
    provedores: [
        {
            provedor: 'anthropic', nome: 'Anthropic (Claude)', buscaNaWeb: true,
            modelos: [
                { id: 'auto', nome: 'Automático', descricao: 'O modelo configurado na plataforma.' },
                { id: 'rapido', nome: 'Rápido', descricao: 'Respostas mais rápidas e baratas.' },
                { id: 'claude-sonnet-5', nome: 'Claude Sonnet 5', descricao: 'Simulado na demonstração.' },
            ],
        },
        {
            provedor: 'openai', nome: 'OpenAI', buscaNaWeb: false,
            modelos: [
                { id: 'auto', nome: 'Automático', descricao: 'O modelo configurado na plataforma.' },
                { id: 'rapido', nome: 'Rápido', descricao: 'Respostas mais rápidas e baratas.' },
            ],
        },
    ],
    padrao: { provedor: 'anthropic', modelo: 'auto' },
    documentos: true,
    voz: { transcrever: true, falar: false },
};

function novoId(prefixo: string): string {
    sequencia += 1;
    return `${prefixo}-${String(sequencia)}`;
}

function copiar<T>(valor: T): T {
    return JSON.parse(JSON.stringify(valor)) as T;
}

/** Proposta pendente velha expira na leitura — é o que o servidor faz no agendador. */
function atualizada(guardada: PropostaGuardada): PropostaDoBrain {
    if (guardada.proposta.situacao === 'pendente' && Date.now() - guardada.criadaEm > VALIDADE_DA_PROPOSTA_MS) {
        guardada.proposta.situacao = 'expirada';
    }
    return guardada.proposta;
}

function comPropostasAtuais(m: MensagemDoBrain): MensagemDoBrain {
    return {
        ...m,
        propostas: m.propostas.map((p) => {
            const guardada = propostas.get(p.id);
            return guardada ? atualizada(guardada) : p;
        }),
    };
}

function citacao(texto: string): string {
    return texto.split('\n').map((linha) => `> ${linha}`).join('\n');
}

function propostaDeAgendamento(conversaId: string, mensagemId: string): PropostaDoBrain {
    const proposta: PropostaDoBrain = {
        id: novoId('proposta'),
        ferramenta: 'agenda_agendar',
        resumo: 'Agendar o retorno de Mariana Costa',
        campos: [
            { rotulo: 'Paciente', valor: 'Mariana Costa' },
            { rotulo: 'Procedimento', valor: 'Retorno de avaliação' },
            { rotulo: 'Data', valor: '10/09/2026' },
            { rotulo: 'Horário', valor: '14:30' },
            { rotulo: 'Profissional', valor: 'Dra. Ana Vecchia' },
        ],
        situacao: 'pendente',
    };
    propostas.set(proposta.id, { conversaId, mensagemId, criadaEm: Date.now(), proposta });
    return proposta;
}

/* O canal 1 da semente de Comunicação, que nasce desconectado. */
function propostaDeReconexao(conversaId: string, mensagemId: string): PropostaDoBrain {
    const proposta: PropostaDoBrain = {
        id: novoId('proposta'),
        ferramenta: 'propor_reconectar_canal',
        resumo: 'Reconectar Canal 01 · Recepção',
        campos: [
            { rotulo: 'Canal', valor: 'Canal 01 · Recepção' },
            { rotulo: 'Situação agora', valor: 'desconectado' },
            { rotulo: 'Como', valor: 'Mostro o QR code aqui para ler no celular' },
        ],
        situacao: 'pendente',
    };
    propostas.set(proposta.id, { conversaId, mensagemId, criadaEm: Date.now(), proposta });
    return proposta;
}

function arquivoDeRelatorio(conversaId: string): ArquivoGeradoDoBrain {
    const id = novoId('arquivo');
    const nome = 'atendimentos-da-semana.csv';
    const caminho = `demonstracao/${conversaId}/${id}-${nome}`;
    const conteudo = 'dia;procedimento;quantidade\n08/09/2026;Limpeza de pele;4\n09/09/2026;Toxina botulínica;2\n';
    arquivos.set(caminho, new Blob([conteudo], { type: 'text/csv' }));
    return { id, nome, tipo: 'text/csv', url: caminho };
}

function responder(conversaId: string, envio: EnvioAoBrain): MensagemDoBrain {
    const pedido = envio.mensagem;
    const id = novoId('mensagem');
    const partes = ['_Resposta de demonstração: nenhuma IA foi chamada e nada saiu deste navegador._', '', 'Você escreveu:', '', citacao(pedido), ''];
    const anexos = envio.anexos ?? [];
    if (anexos.length > 0) {
        partes.push(`Recebi ${anexos.map((a) => `**${a.nome}**`).join(', ')}. Com o servidor ligado, eu leria o conteúdo.`, '');
    }

    const arquivosGerados: ArquivoGeradoDoBrain[] = [];
    const propostasFeitas: PropostaDoBrain[] = [];
    if (/reconect/i.test(pedido)) {
        propostasFeitas.push(propostaDeReconexao(conversaId, id));
        partes.push('O Canal 01 · Recepção está desconectado. Quer reconectar? **Confirme e eu mostro o QR code aqui.**');
    } else if (/agend/i.test(pedido)) {
        propostasFeitas.push(propostaDeAgendamento(conversaId, id));
        partes.push('Preparei a ação abaixo. **Nada acontece sem a sua confirmação.**');
    } else if (/relat|planilha|arquivo|export/i.test(pedido)) {
        arquivosGerados.push(arquivoDeRelatorio(conversaId));
        partes.push('Gerei a planilha abaixo com os atendimentos da semana.');
    } else {
        partes.push(
            'Com o servidor ligado, eu posso:',
            '',
            '- responder sobre agenda, contatos, vendas e financeiro;',
            '- ler PDF, Word, planilha, XML de nota fiscal e imagem;',
            '- gerar arquivos;',
            '- **propor** ações nos módulos, que só executam com a sua confirmação.',
        );
    }

    return {
        id,
        papel: 'assistente',
        conteudo: partes.join('\n'),
        criadoEm: new Date().toISOString(),
        anexos: [],
        arquivos: arquivosGerados,
        propostas: propostasFeitas,
        provedor: envio.provedor ?? 'anthropic',
        modelo: envio.modelo ?? 'auto',
    };
}

function propostaGuardada(id: string): PropostaGuardada {
    const guardada = propostas.get(id);
    if (!guardada) throw new Error('Proposta não encontrada.');
    return guardada;
}

function esperar(ms: number, sinal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (sinal?.aborted) { reject(new DOMException('Espera interrompida.', 'AbortError')); return; }
        if (ms <= 0) { resolve(); return; }
        const t = setTimeout(resolve, ms);
        sinal?.addEventListener('abort', () => {
            clearTimeout(t);
            reject(new DOMException('Espera interrompida.', 'AbortError'));
        }, { once: true });
    });
}

/** Aplica "editar e reenviar": apaga a mensagem da pessoa e tudo depois dela. */
function cortarParaEdicao(conversaId: string, mensagemId: string): number {
    const lista = mensagens.get(conversaId) ?? [];
    const pos = lista.findIndex((m) => m.id === mensagemId && m.papel === 'usuario');
    if (pos < 0) throw new ErroDoBrain(422, 'A mensagem a editar não é sua nesta conversa.', { motivo: 'mensagem_invalida' });
    const depois = lista.slice(pos);
    const decidida = depois.some((m) => m.propostas.some((p) => {
        const s = propostas.get(p.id)?.proposta.situacao ?? p.situacao;
        return s === 'confirmada' || s === 'falhou';
    }));
    if (decidida) {
        throw new ErroDoBrain(409, 'Depois desta mensagem há uma ação já decidida.', { motivo: 'proposta_decidida' });
    }
    for (const m of depois) for (const p of m.propostas) propostas.delete(p.id);
    mensagens.set(conversaId, lista.slice(0, pos));
    return depois.length;
}

export const brainNaDemonstracao: MetodosDoBrain = {
    brainDemonstracao(): boolean {
        return true;
    },

    async brainConversas(filtro?: { projetoId: string | null }): Promise<ConversaDoBrain[]> {
        const agora = Date.now();
        return copiar(conversas
            .filter((c) => Date.parse(c.expiraEm) > agora)
            .filter((c) => !filtro || c.projetoId === filtro.projetoId)
            .sort((a, b) => b.atualizadoEm.localeCompare(a.atualizadoEm)));
    },

    async brainMensagens(conversaId: string): Promise<MensagemDoBrain[]> {
        if (!conversas.some((c) => c.id === conversaId)) throw new Error('Conversa não encontrada.');
        return copiar((mensagens.get(conversaId) ?? []).map(comPropostasAtuais));
    },

    async brainEnviar(envio: EnvioAoBrain, sinal?: AbortSignal): Promise<RespostaDoBrain> {
        const texto = envio.mensagem.trim();
        if (!texto) throw new ErroDoBrain(422, 'Escreva a mensagem antes de enviar.', { motivo: 'mensagem_vazia' });

        const guardada = envio.pedidoId ? pedidos.get(envio.pedidoId) : undefined;
        if (guardada) return copiar({ ...guardada, repetido: true });

        const agora = new Date();
        let conversa = envio.conversaId ? conversas.find((c) => c.id === envio.conversaId) : undefined;
        if (envio.conversaId && !conversa) throw new ErroDoBrain(404, 'Conversa não encontrada.');
        if (!conversa && envio.projetoId && !projetos.some((p) => p.id === envio.projetoId)) {
            throw new ErroDoBrain(404, 'Projeto não encontrado.');
        }
        let removidas = 0;
        if (envio.editarAPartirDe) {
            if (!conversa) throw new ErroDoBrain(422, 'Editar exige a conversa.', { motivo: 'sem_conversa' });
            removidas = cortarParaEdicao(conversa.id, envio.editarAPartirDe);
        }
        if (!conversa) {
            conversa = {
                id: novoId('conversa'),
                titulo: texto.length > 60 ? `${texto.slice(0, 57)}…` : texto,
                criadoEm: agora.toISOString(),
                atualizadoEm: agora.toISOString(),
                expiraEm: new Date(agora.getTime() + RETENCAO_MS).toISOString(),
                projetoId: envio.projetoId ?? null,
            };
            conversas.push(conversa);
        }

        const daPessoa: MensagemDoBrain = {
            id: novoId('mensagem'),
            papel: 'usuario',
            conteudo: texto,
            criadoEm: agora.toISOString(),
            anexos: copiar(envio.anexos ?? []),
            arquivos: [],
            propostas: [],
        };
        const resposta = responder(conversa.id, { ...envio, mensagem: texto });
        mensagens.set(conversa.id, [...(mensagens.get(conversa.id) ?? []), daPessoa, resposta]);
        conversa.atualizadoEm = agora.toISOString();
        conversa.expiraEm = new Date(agora.getTime() + RETENCAO_MS).toISOString();

        const avisos: string[] = [];
        const fontes = [];
        if (envio.buscarNaWeb) {
            if (envio.provedor === 'openai') {
                avisos.push('A busca na web não está disponível com OpenAI na VecchIA; a resposta usou só o que o modelo já sabe.');
            } else {
                fontes.push({ titulo: 'Fonte de demonstração', url: 'https://example.com/demonstracao' });
            }
        }
        const documentos = envio.buscarNosDocumentos ? [{ titulo: 'Documento de demonstração' }] : [];

        const pronta: RespostaDoBrain = {
            conversaId: conversa.id,
            mensagem: { ...copiar(resposta), fontes, documentos, avisos },
            consumo: { tokens: 0 },
            pedidoId: envio.pedidoId ?? null,
            repetido: false,
            mensagemDaPessoa: { id: daPessoa.id, criadoEm: daPessoa.criadoEm },
            modelo: { provedor: resposta.provedor ?? 'anthropic', modelo: resposta.modelo ?? 'auto' },
            fontes,
            documentos,
            avisos,
            removidas,
            fala: envio.modoVoz ? 'Esta é uma resposta de demonstração, sem IA. Deixei a resposta completa no chat para você conferir.' : null,
        };
        /* O servidor termina e guarda mesmo quando a tela para de esperar. */
        if (envio.pedidoId) pedidos.set(envio.pedidoId, copiar(pronta));
        await esperar(atrasoMs, sinal);
        return copiar(pronta);
    },

    async brainPedido(pedidoId: string): Promise<PedidoDoBrain> {
        const pronta = pedidos.get(pedidoId);
        if (!pronta) return { situacao: 'desconhecido' };
        const lista = mensagens.get(pronta.conversaId) ?? [];
        const pos = lista.findIndex((m) => m.id === pronta.mensagemDaPessoa?.id);
        return {
            situacao: 'pronta',
            conversaId: pronta.conversaId,
            mensagens: copiar((pos >= 0 ? lista.slice(pos, pos + 2) : [pronta.mensagem]).map(comPropostasAtuais)),
            fala: pronta.fala,
        };
    },

    async brainEnviarAnexo(arquivo: File, conversaId?: string): Promise<AnexoDoBrain> {
        const problema = problemaDoAnexo(arquivo);
        if (problema) throw new Error(problema);
        const caminho = `demonstracao/${conversaId ?? 'rascunho'}/${novoId('anexo')}-${arquivo.name}`;
        arquivos.set(caminho, arquivo);
        return { caminho, nome: arquivo.name, tipo: arquivo.type };
    },

    async brainConfirmarProposta(id: string): Promise<PropostaDoBrain> {
        const guardada = propostaGuardada(id);
        const proposta = atualizada(guardada);
        if (proposta.situacao === 'pendente') {
            proposta.situacao = 'confirmada';
            if (proposta.ferramenta === 'propor_reconectar_canal') {
                proposta.resultado = 'Leia o QR code abaixo com o WhatsApp de Canal 01 · Recepção.';
                proposta.canalId = 'canal-1';
            } else {
                proposta.resultado = 'Demonstração: nada foi gravado. Com o servidor ligado, o agendamento seria criado com estes campos.';
            }
        }
        return copiar(proposta);
    },

    async brainCancelarProposta(id: string): Promise<PropostaDoBrain> {
        const proposta = atualizada(propostaGuardada(id));
        if (proposta.situacao === 'pendente') proposta.situacao = 'cancelada';
        return copiar(proposta);
    },

    async brainApagarConversa(id: string): Promise<void> {
        if (!conversas.some((c) => c.id === id)) throw new Error('Conversa não encontrada.');
        conversas = conversas.filter((c) => c.id !== id);
        mensagens.delete(id);
        for (const [chave, guardada] of propostas) {
            if (guardada.conversaId === id) propostas.delete(chave);
        }
        for (const caminho of [...arquivos.keys()]) {
            if (caminho.startsWith(`demonstracao/${id}/`)) arquivos.delete(caminho);
        }
    },

    async brainRenomearConversa(id: string, titulo: string): Promise<void> {
        const conversa = conversas.find((c) => c.id === id);
        if (!conversa) throw new Error('Conversa não encontrada.');
        const limpo = titulo.trim().slice(0, 120);
        if (!limpo) throw new Error('Dê um nome à conversa.');
        conversa.titulo = limpo;
    },

    async brainMoverConversa(id: string, projetoId: string | null): Promise<void> {
        const conversa = conversas.find((c) => c.id === id);
        if (!conversa) throw new Error('Conversa não encontrada.');
        if (projetoId && !projetos.some((p) => p.id === projetoId)) throw new ErroDoBrain(404, 'Projeto não encontrado.');
        conversa.projetoId = projetoId;
    },

    async brainProjetos(): Promise<ProjetoDoBrain[]> {
        return copiar([...projetos].sort((a, b) => b.criadoEm.localeCompare(a.criadoEm)));
    },

    async brainCriarProjeto(nome: string): Promise<ProjetoDoBrain> {
        const limpo = nome.trim().slice(0, 80);
        if (!limpo) throw new Error('Informe o nome do projeto.');
        const projeto: ProjetoDoBrain = { id: novoId('projeto'), nome: limpo, criadoEm: new Date(Date.now() + sequencia).toISOString() };
        projetos.push(projeto);
        return copiar(projeto);
    },

    async brainRenomearProjeto(id: string, nome: string): Promise<void> {
        const projeto = projetos.find((p) => p.id === id);
        if (!projeto) throw new Error('Projeto não encontrado.');
        const limpo = nome.trim().slice(0, 80);
        if (!limpo) throw new Error('Informe o nome do projeto.');
        projeto.nome = limpo;
    },

    async brainArquivarProjeto(id: string): Promise<void> {
        if (!projetos.some((p) => p.id === id)) throw new Error('Projeto não encontrado.');
        projetos = projetos.filter((p) => p.id !== id);
    },

    async brainPreferencias(): Promise<PreferenciasDoBrain> {
        return copiar(preferencias);
    },

    async brainSalvarPreferencias(novas: PreferenciasDoBrain): Promise<PreferenciasDoBrain> {
        preferencias = normalizarPreferencias(novas);
        return copiar(preferencias);
    },

    async brainModelos(): Promise<ModelosDoBrain> {
        return copiar(MODELOS_DA_DEMONSTRACAO);
    },

    async brainTexto(acao: AcaoDeTexto, texto: string, idioma?: IdiomaDoBrain): Promise<string> {
        const limpo = texto.trim();
        if (!limpo) throw new ErroDoBrain(422, 'Digite um texto no campo antes de usar a IA.', { motivo: 'texto_vazio' });
        const aviso = '[Demonstração, sem IA] ';
        switch (acao) {
            case 'gramatica':
                return `${aviso}${limpo.charAt(0).toUpperCase()}${limpo.slice(1)}${/[.!?]$/.test(limpo) ? '' : '.'}`;
            case 'melhorar':
                return `${aviso}${limpo.replace(/\s+/g, ' ')}`;
            case 'escrever':
                return `${aviso}Texto escrito a partir do pedido: ${limpo}`;
            case 'traduzir':
                return `${aviso}(${idioma ?? preferencias.idioma}) ${limpo}`;
            case 'intencao':
                /* Sem IA na demonstração: a dúvida não decide nada. */
                return 'outra';
        }
    },

    async brainAprenderLink(url: string): Promise<LinkAprendido> {
        if (!/^https?:\/\//i.test(url.trim())) {
            throw new ErroDoBrain(422, 'Use um endereço que comece com http:// ou https://.', { motivo: 'endereco_recusado' });
        }
        return {
            url: url.trim(),
            titulo: 'Página de demonstração',
            tipo: 'text/html',
            texto: 'Demonstração: sem servidor, nenhuma página foi lida. Com a aplicação ligada ao servidor, o texto da página entra aqui.',
            truncado: false,
        };
    },

    async brainTranscrever(audio: Blob, idioma?: IdiomaDoBrain): Promise<string> {
        if (audio.size === 0) throw new Error('A gravação ficou vazia: tente de novo, falando perto do microfone.');
        if (idioma === 'en') return 'What are tomorrow’s appointments?';
        if (idioma === 'es') return '¿Cuáles son las citas de mañana?';
        return 'Quais são os agendamentos de amanhã?';
    },

    /* Sem servidor não há síntese de voz. Tocar silêncio fingiria que ouvir
       funciona; a recusa diz o que falta. */
    async brainFalar(): Promise<FalaGerada> {
        throw new Error('A demonstração não tem voz: ouvir a resposta só existe com a aplicação ligada ao servidor.');
    },

    async brainAbrirArquivo(caminho: string): Promise<string> {
        const conteudo = arquivos.get(caminho);
        if (!conteudo) throw new Error('Arquivo não encontrado.');
        if (typeof URL.createObjectURL !== 'function') throw new Error('Este navegador não abre arquivos da demonstração.');
        return URL.createObjectURL(conteudo);
    },
};
